/**
 * CashierCodeEntry Component (Story 7.2)
 *
 * 4-digit numeric code entry for cashier identification before processing redemptions.
 * iPad-optimized with large touch targets and numeric keyboard.
 *
 * Features:
 * - 4 individual digit input boxes
 * - Auto-focus and auto-advance
 * - Numeric keyboard on mobile
 * - Clear error states
 * - Loading indicator during validation
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Loader, AlertCircle, User } from 'lucide-react';
import { useTranslation } from '@/i18n';

interface CashierInfo {
	cashierId: number;
	cashierName: string;
	code: string;
}

interface CashierCodeEntryProps {
	onValidated: (cashierInfo: CashierInfo) => void;
	onValidate: (code: string) => Promise<{
		valid: boolean;
		cashier_id?: number;
		cashier_name?: string;
		error_code?: 'INVALID_CODE' | 'DISABLED_CODE';
	}>;
	isValidating?: boolean;
}

const CashierCodeEntry: React.FC<CashierCodeEntryProps> = ({
	onValidated,
	onValidate,
	isValidating = false,
}) => {
	const { t } = useTranslation();
	const [digits, setDigits] = useState<string[]>(['', '', '', '']);
	const [error, setError] = useState<string | null>(null);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	// Focus first input on mount
	useEffect(() => {
		inputRefs.current[0]?.focus();
	}, []);

	const handleSubmit = useCallback(async (code: string) => {
		if (code.length !== 4) return;

		try {
			const result = await onValidate(code);

			if (result.valid && result.cashier_id && result.cashier_name) {
				onValidated({
					cashierId: result.cashier_id,
					cashierName: result.cashier_name,
					code,
				});
			} else {
				// Show error and reset
				if (result.error_code === 'INVALID_CODE') {
					setError(t('merchant.cashierCode.errors.invalidCode'));
				} else if (result.error_code === 'DISABLED_CODE') {
					setError(t('merchant.cashierCode.errors.disabledCode'));
				} else {
					setError(t('merchant.cashierCode.errors.validationFailed'));
				}
				setDigits(['', '', '', '']);
				inputRefs.current[0]?.focus();
			}
		} catch {
			setError(t('merchant.cashierCode.errors.validationFailed'));
			setDigits(['', '', '', '']);
			inputRefs.current[0]?.focus();
		}
	}, [onValidate, onValidated, t]);

	const handleDigitChange = useCallback(
		(index: number, value: string) => {
			// Only allow single digit
			const digit = value.replace(/\D/g, '').slice(-1);

			const newDigits = [...digits];
			newDigits[index] = digit;
			setDigits(newDigits);
			setError(null);

			// Auto-advance to next input
			if (digit && index < 3) {
				inputRefs.current[index + 1]?.focus();
			}

			// Auto-submit when all 4 digits entered
			if (digit && index === 3) {
				const code = newDigits.join('');
				if (code.length === 4) {
					void handleSubmit(code);
				}
			}
		},
		[digits, handleSubmit]
	);

	const handleKeyDown = useCallback(
		(index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
			// Handle backspace - go to previous input
			if (e.key === 'Backspace' && !digits[index] && index > 0) {
				inputRefs.current[index - 1]?.focus();
			}
		},
		[digits]
	);

	const handlePaste = useCallback((e: React.ClipboardEvent) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);

		if (pastedData.length === 4) {
			const newDigits = pastedData.split('');
			setDigits(newDigits);
			setError(null);
			void handleSubmit(pastedData);
		}
	}, [handleSubmit]);

	const handleFormSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		const code = digits.join('');
		if (code.length === 4) {
			void handleSubmit(code);
		}
	};

	return (
		<div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8'>
			<div className='text-center max-w-md mx-auto'>
				{/* Header */}
				<div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4'>
					<User className='w-8 h-8 text-blue-600' />
				</div>
				<h2 className='text-xl sm:text-2xl font-semibold text-gray-900 mb-2'>
					{t('merchant.cashierCode.title')}
				</h2>
				<p className='text-gray-600 mb-6'>
					{t('merchant.cashierCode.subtitle')}
				</p>

				{/* Code Entry Form */}
				<form onSubmit={handleFormSubmit}>
					<div className='flex justify-center gap-3 sm:gap-4 mb-4'>
						{digits.map((digit, index) => (
							<input
								key={index}
								ref={el => {
									inputRefs.current[index] = el;
								}}
								type='text'
								inputMode='numeric'
								pattern='[0-9]*'
								maxLength={1}
								value={digit}
								onChange={e => handleDigitChange(index, e.target.value)}
								onKeyDown={e => handleKeyDown(index, e)}
								onPaste={index === 0 ? handlePaste : undefined}
								disabled={isValidating}
								className={`
									w-14 h-16 sm:w-16 sm:h-20
									text-center text-2xl sm:text-3xl font-bold
									border-2 rounded-lg
									focus:ring-2 focus:ring-blue-500 focus:border-blue-500
									disabled:bg-gray-100 disabled:cursor-not-allowed
									transition-colors
									${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}
								`}
								aria-label={t('merchant.cashierCode.digitLabel', { number: index + 1 })}
							/>
						))}
					</div>

					{/* Error Message */}
					{error && (
						<div className='flex items-center justify-center gap-2 text-red-600 mb-4'>
							<AlertCircle className='w-5 h-5' />
							<span className='text-sm font-medium'>{error}</span>
						</div>
					)}

					{/* Loading State */}
					{isValidating && (
						<div className='flex items-center justify-center gap-2 text-blue-600 mb-4'>
							<Loader className='w-5 h-5 animate-spin' />
							<span className='text-sm'>{t('merchant.cashierCode.validating')}</span>
						</div>
					)}

					{/* Submit Button (for accessibility, auto-submit on 4 digits) */}
					<button
						type='submit'
						disabled={digits.join('').length !== 4 || isValidating}
						className='w-full sm:w-auto px-8 py-3 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
					>
						{isValidating ? t('merchant.cashierCode.validating') : t('merchant.cashierCode.submit')}
					</button>
				</form>

				{/* Help Text */}
				<p className='text-xs text-gray-500 mt-4'>
					{t('merchant.cashierCode.helpText')}
				</p>
			</div>
		</div>
	);
};

export default CashierCodeEntry;
